import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as criminalService from '../services/criminalCaseRequests';
import type { CriminalWizardDraft } from '../types/criminalCaseRequest';
import * as civilAdviseService from '../services/civilAdviseCaseRequests';
import * as civilLitigationService from '../services/civilLitigationCaseRequests';
import * as criminalAppellateService from '../services/criminalAppellateCaseRequests';
import * as extraditionMlaService from '../services/extraditionMlaCaseRequests';
import * as nominationService from '../services/nominationCaseRequests';
import * as publicPetitionService from '../services/publicPetitionCaseRequests';
import * as rightToInformationService from '../services/rightToInformationCaseRequests';
import * as attorneyGeneralService from '../services/attorneyGeneralCaseRequests';
import * as civilAppellateService from '../services/civilAppellateCaseRequests';
import { setFlashToast } from '../utils/flashToast';
import { wizardFlowForCategoryName, type WizardFlowKind } from '../utils/wizardCategoryRoute';
import { civilAdviseWizardPath } from '../pages/wizard/civilAdvise/CivilAdviseWizardShell';
import { civilAppellateWizardPath } from '../pages/wizard/civilAppellate/CivilAppellateWizardShell';
import { civilLitigationWizardPath } from '../pages/wizard/civilLitigation/CivilLitigationWizardShell';
import { criminalWizardPath } from '../pages/wizard/criminal/CriminalWizardShell';
import { criminalAppellateWizardPath } from '../pages/wizard/criminalAppellate/CriminalAppellateWizardShell';
import { nominationWizardPath } from '../pages/wizard/nomination/NominationWizardShell';
import { publicPetitionWizardPath } from '../pages/wizard/publicPetition/publicPetitionWizardShell';
import { rightToInformationWizardPath } from '../pages/wizard/rightToInformation/RightToInformationWizardShell';
import { attorneyGeneralWizardPath } from '../pages/wizard/attorneyGeneral/AttorneyGeneralWizardShell';
import { extraditionMlaWizardPath } from '../pages/wizard/extraditionMla/ExtraditionMlaWizardShell';

export type ContinueDraftInput = {
  id?: string;
  ref: string;
  cat?: string;
  fileCategory?: string;
};

export function useContinueDraftCase(returnTo: string) {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [continueDraftId, setContinueDraftId] = useState<string | null>(null);

  const handleContinueDraft = useCallback(
    (r: ContinueDraftInput) => {
      const id = (r.id ?? r.ref).trim();
      if (!id || continueDraftId !== null) return;
      setContinueDraftId(id);

      const flow = wizardFlowForCategoryName(r.cat ?? r.fileCategory ?? '');
      const finish = (
        path: string,
        message?: string,
        resumeFlow?: WizardFlowKind,
        seedDraft?: Partial<CriminalWizardDraft>,
      ) => {
        if (message) {
          setFlashToast({ message, variant: 'error' });
          return;
        }
        const state = {
          returnTo,
          ...(resumeFlow && resumeFlow !== 'unknown' ? { resumeFlow } : {}),
          ...(seedDraft ? { seedDraft } : {}),
        };
        nav(path, { state });
      };

      if (flow === 'extradition-mla') {
        void extraditionMlaService
          .getExtraditionMlaDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(extraditionMlaWizardPath(res.data.step, res.data.requestId), undefined, 'extradition-mla');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'civil-appellate') {
        void civilAppellateService
          .getCivilAppellateDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(civilAppellateWizardPath(res.data.step, res.data.requestId), undefined, 'civil-appellate');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'civil-litigation') {
        void civilLitigationService
          .getCivilLitigationDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(civilLitigationWizardPath(res.data.step, res.data.requestId), undefined, 'civil-litigation');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'civil-advise') {
        void civilAdviseService
          .getCivilAdviseDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(civilAdviseWizardPath(res.data.step, res.data.requestId), undefined, 'civil-advise');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'nomination-requests') {
        void nominationService
          .getNominationDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(nominationWizardPath(res.data.step, res.data.requestId), undefined, 'nomination-requests');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'public-petition') {
        void publicPetitionService
          .getPublicPetitionDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(publicPetitionWizardPath(res.data.step, res.data.requestId), undefined, 'public-petition');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'right-to-information') {
        void rightToInformationService
          .getRightToInformationDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(
              rightToInformationWizardPath(res.data.step, res.data.requestId),
              undefined,
              'right-to-information',
            );
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'attorney-general') {
        void attorneyGeneralService
          .getAttorneyGeneralDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(attorneyGeneralWizardPath(res.data.step, res.data.requestId), undefined, 'attorney-general');
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      if (flow === 'criminal-appellate') {
        void criminalAppellateService
          .getCriminalAppellateDraftResumeStep(id)
          .then((res) => {
            if (!res.success || !res.data) {
              finish('', res.message ?? t('caseRequests.loadDraftFailed'));
              return;
            }
            finish(criminalAppellateWizardPath(res.data.step, res.data.requestId));
          })
          .finally(() => setContinueDraftId(null));
        return;
      }

      void criminalService
        .getCriminalDraftResume(id)
        .then((res) => {
          if (!res.success || !res.data) {
            if (flow === 'unknown') {
              void extraditionMlaService.getExtraditionMlaDraftResumeStep(id).then((emla) => {
                if (emla.success && emla.data) {
                  finish(extraditionMlaWizardPath(emla.data.step, emla.data.requestId), undefined, 'extradition-mla');
                  return;
                }
                void civilAppellateService.getCivilAppellateDraftResumeStep(id).then((app) => {
                  if (app.success && app.data) {
                    finish(civilAppellateWizardPath(app.data.step, app.data.requestId), undefined, 'civil-appellate');
                    return;
                  }
                  void civilLitigationService.getCivilLitigationDraftResumeStep(id).then((lit) => {
                    if (lit.success && lit.data) {
                      finish(civilLitigationWizardPath(lit.data.step, lit.data.requestId), undefined, 'civil-litigation');
                      return;
                    }
                    void civilAdviseService.getCivilAdviseDraftResumeStep(id).then((adv) => {
                      if (adv.success && adv.data) {
                        finish(civilAdviseWizardPath(adv.data.step, adv.data.requestId), undefined, 'civil-advise');
                        return;
                      }
                      finish('', res.message ?? t('caseRequests.loadDraftFailed'));
                    });
                  });
                });
              });
              return;
            }
            finish('', res.message ?? t('caseRequests.loadDraftFailed'));
            return;
          }
          finish(criminalWizardPath(res.data.step, res.data.requestId), undefined, 'criminal', res.data.draft);
        })
        .finally(() => setContinueDraftId(null));
    },
    [continueDraftId, nav, returnTo, t],
  );

  return { continueDraftId, handleContinueDraft };
}
